import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ResponsiveActionCard from '~/Nova/scenes/Site/components/ResponsiveActionCard';

import { validateApiKeyName } from '../../utils/helpers';
import { CreateApiKeyViewProps } from '../../utils/types';
import ModalHeader from './ModalHeader';

const CreateApiKeyView = ({
  name,
  onApiKeyNameChange,
  onSubmit,
  cancelCreate,
  onClose,
  isSubmitting = false,
}: CreateApiKeyViewProps) => {
  const { t } = useTranslation('aiTooling', {
    keyPrefix: 'apiKeys.modal.create',
  });
  const [hasBlurred, setHasBlurred] = useState(false);

  const validationError = validateApiKeyName(name);
  const trimmedApiKeyName = name.trim();
  const showError =
    validationError !== null &&
    (trimmedApiKeyName.length > 0 ||
      (hasBlurred && validationError !== 'required'));

  return (
    <>
      <ModalHeader title={t('title')} onBack={cancelCreate} onClose={onClose} />
      <DialogContent>
        <Stack spacing={4} sx={{ pt: 2 }}>
          <ResponsiveActionCard
            title={t('nameLabel')}
            dataTestId="create-key-card"
          >
            <TextField
              fullWidth
              size="small"
              autoFocus
              value={name}
              onChange={(e) => onApiKeyNameChange(e.target.value)}
              onBlur={() => setHasBlurred(true)}
              placeholder={t('namePlaceholder')}
              error={showError}
              helperText={
                showError && validationError
                  ? t(`validation.${validationError}`)
                  : t('validation.hint')
              }
              data-testid="create-api-key-name-input"
            />
          </ResponsiveActionCard>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            alignItems="center"
            sx={{ mt: 2.5 }}
          >
            <Button
              variant="text"
              onClick={cancelCreate}
              data-testid="create-api-key-cancel-btn"
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              disabled={validationError !== null || isSubmitting}
              onClick={onSubmit}
              data-testid="create-api-key-submit-btn"
            >
              {t('submit')}
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );
};

export default CreateApiKeyView;
